import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { MelodyStaffPayload, MelodyStaffRuntime } from '../../core/types/payloads/melody_staff.types';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import { pitchToSpanishLabel } from '../../core/music/staffRender';
import { isMusicalToken } from '../../core/music/tokens';
import type { MusicalToken } from '../../types/music';

export function validateMelodyStaffPayload(raw: unknown): MelodyStaffPayload {
  if (!raw || typeof raw !== 'object') throw new Error('MelodyStaff payload must be an object');
  const data = raw as Partial<MelodyStaffPayload>;
  if (!data.title) throw new Error('MelodyStaff requires title');

  const notes = Array.isArray(data.notes) ? data.notes : [];
  if (notes.length < 1) throw new Error('MelodyStaff requires at least one note');

  const normalizedNotes = notes.map((note, i) => ({
    id: String(note?.id || `n${i + 1}`),
    pitch: String(note?.pitch || 'G4'),
    duration: (isMusicalToken(String(note?.duration)) ? String(note.duration) : 'Q') as MusicalToken,
    accidental: note?.accidental,
  }));

  const highlightNoteId = String(data.highlightNoteId || normalizedNotes[0].id);

  const taskKind =
    data.taskKind === 'transcribe' || data.taskKind === 'locate' ? data.taskKind : 'identify';

  const density = data.density === 'comfortable' ? 'comfortable' : 'compact';
  // Ítems mecánicos: acotar a 4 notas para no ocupar una frase completa.
  const cappedNotes =
    density === 'compact' && normalizedNotes.length > 4
      ? normalizedNotes.slice(0, 4)
      : normalizedNotes;
  const highlight = cappedNotes.find((n) => n.id === highlightNoteId) ?? cappedNotes[0];

  return {
    title: String(data.title),
    clef: data.clef === 'bass' ? 'bass' : 'treble',
    timeSignature:
      data.timeSignature === '3/4' || data.timeSignature === '2/4' ? data.timeSignature : '4/4',
    notes: cappedNotes,
    taskKind,
    prompt: String(data.prompt || 'Identifica la nota señalada en el pentagrama:').trim(),
    highlightNoteId: highlight.id,
    answerLabel: String(data.answerLabel || pitchToSpanishLabel(highlight.pitch)).trim(),
    density,
  };
}

export function createInitialMelodyStaffRuntime(
  _payload: MelodyStaffPayload,
  _routing: RoutingMode,
): MelodyStaffRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0 } };
}

export const melodyStaffLogic: ActivityLogicPlugin<'melody_staff'> = {
  mode: 'melody_staff',
  validatePayload: validateMelodyStaffPayload,
  createInitialRuntime: createInitialMelodyStaffRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'FILL') {
      const value = String((input.data as { value?: string })?.value ?? '');
      return {
        runtime: {
          ...ctx.runtime,
          responses: { ...ctx.runtime.responses, [participant.participantId]: value },
        },
      };
    }
    if (input.action === 'SUBMIT') {
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const submitted = Object.keys(finalized).filter((id) => finalized[id]).length;
      return {
        runtime: { ...ctx.runtime, finalized, aggregates: { submitted } },
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoMelodyStaffPayload(): MelodyStaffPayload {
  return validateMelodyStaffPayload({
    title: 'Nota en el pentagrama',
    clef: 'treble',
    timeSignature: '4/4',
    notes: [{ id: 'n1', pitch: 'G4', duration: 'Q' }],
    taskKind: 'identify',
    highlightNoteId: 'n1',
    prompt: '¿Qué nota está escrita en el pentagrama?',
    answerLabel: 'Sol',
  });
}
